/**
 * 配置路由守卫
 * @description 检查系统配置开关状态，拦截不可用功能
 * @since 2025-12-27
 */

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useSystemConfigStore } from '@/store/modules/common/systemConfig'

/**
 * 配置检查路由守卫
 * - 确保公开配置已加载
 * - 检查维护模式
 * - 检查功能开关
 */
export async function configGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  const configStore = useSystemConfigStore()

  // 确保公开配置已加载
  if (!configStore.publicLoaded) {
    await configStore.fetchPublicConfig()
  }

  // 维护模式页面不需要检查
  if (to.name === 'MaintenancePage') {
    next()
    return
  }

  // 检查维护模式
  if (configStore.isMaintenanceMode) {
    next({ name: 'MaintenancePage' })
    return
  }

  // 检查功能开关
  if (to.meta.requiresUpload && !configStore.canUpload) {
    ElMessage.warning('模型上传服务暂时关闭，请稍后再试')
    next(false)
    return
  }

  if (to.meta.requiresPublish && !configStore.canPublish) {
    ElMessage.warning('任务发布服务暂时关闭，请稍后再试')
    next(false)
    return
  }

  if (to.meta.requiresWithdraw && !configStore.canWithdraw) {
    ElMessage.warning('提现服务暂时关闭，请稍后再试')
    next(false)
    return
  }

  if (to.meta.requiresRegister && !configStore.canRegister) {
    ElMessage.warning('注册服务暂时关闭，请稍后再试')
    next(false)
    return
  }

  next()
}

/**
 * 扩展路由meta类型
 */
declare module 'vue-router' {
  interface RouteMeta {
    /** 需要上传功能可用 */
    requiresUpload?: boolean
    /** 需要发布功能可用 */
    requiresPublish?: boolean
    /** 需要提现功能可用 */
    requiresWithdraw?: boolean
    /** 需要注册功能可用 */
    requiresRegister?: boolean
  }
}
